/**
 * TopBar — the canonical top bar.
 *
 *   left:   breadcrumb + optional workflow-stage indicator slot
 *   center: global search (mock; no-op placeholder filter)
 *   right:  ROLE switcher + HOSPITAL switcher + user menu
 *
 * The hospital switcher calls `setTenant(id)` (which drives `apiClient.setTenant`
 * and persists) so multi-tenancy (FND-003) is visibly demonstrated. The role
 * switcher swaps the active role label (and re-scopes role-aware queues).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors, radius } from '../theme';
import { useAuth } from '../auth/useAuth';
import { roleLabel } from './RoleBadge';
import { HOSPITALS, flattenNav } from './nav';
import { useShellSlots } from './shellContext';
import {
  ROLE_VIEWS,
  ROLE_VIEW_ORDER,
  isRoleViewKey,
  type RoleViewKey,
} from './roleViews';

/** Derive a default breadcrumb label from the current path (parents + sub-pages). */
function pathLabel(pathname: string): string {
  const top = '/' + (pathname.split('/')[1] ?? '');
  return flattenNav().find((n) => n.to === top)?.label ?? 'Home';
}

export function TopBar() {
  const { user, logout, tenantId, setTenant, activeRole, activeView, setActiveView } = useAuth();
  const { breadcrumb, stage } = useShellSlots();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const section = pathLabel(location.pathname);

  // Role-first scoping: selecting a view trims the nav AND lands on the view's
  // home. This is UI-only — `setActiveView` never touches the token/tenant, so
  // a scoped persona (e.g. CFO) keeps calling the API as the logged-in staff
  // user and never 403s.
  function onViewChange(key: RoleViewKey) {
    setActiveView(key);
    navigate(ROLE_VIEWS[key].landing);
  }

  return (
    <header style={bar}>
      {/* Left: breadcrumb + workflow stage slot */}
      <div style={left}>
        <nav style={crumbs} aria-label="Breadcrumb">
          <span style={crumbMuted}>Enable</span>
          <span style={crumbSep}>/</span>
          <span style={breadcrumb ? crumbMuted : crumbCurrent}>{section}</span>
          {breadcrumb && (
            <>
              <span style={crumbSep}>/</span>
              <span style={crumbCurrent}>{breadcrumb}</span>
            </>
          )}
        </nav>
        {stage && <div style={stageSlot}>{stage}</div>}
      </div>

      {/* Center: global search (mock) */}
      <div style={center}>
        <span style={searchIcon} aria-hidden>
          ⌕
        </span>
        <input
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search cases, employers, invoices…"
          aria-label="Global search"
          style={searchInput}
        />
      </div>

      {/* Right: role + hospital switcher + user menu */}
      <div style={right}>
        <label style={switcher}>
          <span style={switcherLabel}>Role</span>
          <select
            value={activeView}
            onChange={(e) => {
              const next = e.target.value;
              if (isRoleViewKey(next)) onViewChange(next);
            }}
            style={select}
            aria-label="Active role view"
          >
            {/* Safety net: if `activeView` isn't a listed option, show a disabled
                placeholder so the control stays valid. `all` is now listed. */}
            {!ROLE_VIEW_ORDER.includes(activeView) && (
              <option value={activeView} disabled>
                Select role…
              </option>
            )}
            {ROLE_VIEW_ORDER.map((key) => (
              <option key={key} value={key}>
                {ROLE_VIEWS[key].label}
              </option>
            ))}
          </select>
        </label>

        <label style={switcher}>
          <span style={switcherLabel}>Hospital</span>
          <select
            value={tenantId}
            onChange={(e) => setTenant(e.target.value)}
            style={select}
            aria-label="Active hospital (tenant)"
          >
            {HOSPITALS.map((h) => (
              <option key={h.id} value={h.id}>
                {h.label}
              </option>
            ))}
          </select>
        </label>

        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            style={userBtn}
            aria-label="User menu"
          >
            <span style={userAvatar}>{user?.name?.charAt(0)?.toUpperCase() ?? 'U'}</span>
          </button>
          {menuOpen && (
            <div style={menu}>
              <div style={menuHeader}>
                <div style={{ fontWeight: 600 }}>{user?.name}</div>
                <div style={menuEmail}>{user?.email}</div>
                <div style={menuRoleRow}>
                  <span style={roleChip}>{roleLabel(activeRole)}</span>
                  <span style={viewChip} title="Active role view">
                    {ROLE_VIEWS[activeView].label}
                  </span>
                </div>
              </div>
              <div style={menuDivider} />
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                style={menuItem}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

const bar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  height: 56,
  padding: '0 20px',
  background: '#FFFFFF',
  borderBottom: `1px solid ${colors.border}`,
  position: 'sticky',
  top: 0,
  zIndex: 50,
  flexShrink: 0,
};
const left: CSSProperties = { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flexShrink: 0 };
const crumbs: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.86rem' };
const crumbMuted: CSSProperties = { color: colors.textSecondary };
const crumbCurrent: CSSProperties = { color: colors.textPrimary, fontWeight: 600 };
const crumbSep: CSSProperties = { color: colors.textMuted };
const stageSlot: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 14,
  borderLeft: `1px solid ${colors.border}`,
};
const center: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  maxWidth: 520,
  margin: '0 auto',
};
const searchIcon: CSSProperties = {
  position: 'absolute',
  left: 12,
  color: colors.textMuted,
  fontSize: '1rem',
  pointerEvents: 'none',
};
const searchInput: CSSProperties = { paddingLeft: 32, background: colors.pageBg };
const right: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 };
const switcher: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 1 };
const switcherLabel: CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textMuted,
};
const select: CSSProperties = {
  padding: '4px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '0.84rem',
  background: '#FFFFFF',
  color: colors.textPrimary,
  fontFamily: 'inherit',
  maxWidth: 180,
};
const userBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
};
const userAvatar: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: colors.brand,
  color: '#FFFFFF',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.85rem',
};
const menu: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 240,
  background: '#FFFFFF',
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  boxShadow: '0 8px 24px rgba(28,25,23,0.12)',
  overflow: 'hidden',
  zIndex: 200,
};
const menuHeader: CSSProperties = { padding: '12px 16px', color: colors.textPrimary };
const menuEmail: CSSProperties = { fontSize: '0.8rem', color: colors.textSecondary, marginTop: 2 };
const menuRoleRow: CSSProperties = { marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' };
const roleChip: CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  fontSize: '0.72rem',
  fontWeight: 600,
  borderRadius: 999,
  background: colors.brandTint,
  color: colors.brandActive,
};
const viewChip: CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  fontSize: '0.72rem',
  fontWeight: 600,
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  color: colors.textSecondary,
};
const menuDivider: CSSProperties = { height: 1, background: colors.border };
const menuItem: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 16px',
  background: 'none',
  border: 'none',
  textAlign: 'left',
  color: colors.textPrimary,
  fontSize: '0.9rem',
  cursor: 'pointer',
};
